/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { LoggerModule } from "@vlinder-web/logger-module-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { VApplication } from "../../../application";

import { GET_TEMPLATE_FEATURES } from "../graphql";

const log: any = LoggerModule?.getInstance();

export interface GetTemplateFeaturesProps {
  presentationId?: string;
  presentation?: string;
  id?: string;
  [key: string]: any;
}

export interface IGetTemplateFeaturesMethods extends IPluginMethodMap {
  getTemplateFeatures(args: GetTemplateFeaturesProps): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getPresentationId = (args: GetTemplateFeaturesProps) => {
  return args?.presentationId ?? args?.presentation ?? args?.id;
};

const mapAsset = (asset: any, assetType: "global" | "section") => ({
  ...asset,
  id: asset?.id ?? asset?._id,
  name: asset?.name ?? "Untitled Feature",
  assetType,
  isCommon: asset?.isCommon === true,
  section: Array.isArray(asset?.section) ? asset.section : [],
});

export class GetTemplateFeaturesService implements IAgentPlugin {
  readonly methods: IGetTemplateFeaturesMethods;

  constructor() {
    this.methods = {
      getTemplateFeatures: this.getTemplateFeatures.bind(this),
    };
  }

  async getTemplateFeatures(args: GetTemplateFeaturesProps) {
    log?.blue?.("GetTemplateFeaturesService.getTemplateFeatures:", args);

    const presentationId = getPresentationId(args);

    if (!presentationId) {
      throw new Error("Missing presentation id");
    }

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    if (!client) {
      throw new Error("GraphQL client not found");
    }

    const res = await client.query({
      query: GET_TEMPLATE_FEATURES,
      variables: {
        presentationId,
        options: {
          sort: {
            field: "position",
            order: "asc",
          },
          limit: 2000,
          skip: 0,
        },
      },
      fetchPolicy: "network-only",
    });

    const raw = res?.data?.getTemplateFeatures ?? {};

    const globalAssets = (raw?.globalAssets || []).map((asset: any) =>
      mapAsset(asset, "global"),
    );

    const sectionAssets = (raw?.sectionAssets || []).map((asset: any) =>
      mapAsset(asset, "section"),
    );

    return {
      globalAssets,
      sectionAssets,
      assets: [...globalAssets, ...sectionAssets],
      isEmpty: globalAssets.length === 0 && sectionAssets.length === 0,
    };
  }
}
